import memoizee from 'memoizee';

export const memoize = <A extends Array<unknown>, R>(func: (...args: A) => R) =>
    memoizee(func, {
        normalizer: (args) => JSON.stringify(args),
        primitive: true,
    });

export const primitiveMemoize = <A extends Array<unknown>, R>(func: (...args: A) => R) =>
    memoizee(func, {
        primitive: true,
    });
/**
 * Creates a WeakMap-based selector for caching computed values, ideal for react-query `select` option.
 * The value is calculated once for each unique state instance, requiring referentially stable state objects.
 * Uses WeakMap to avoid blocking garbage collection of state objects, making it ideal when the cache
 * is maintained outside of this function (e.g., query cache).
 *
 * Usage: Create the selector outside of the query hook, ideally have at most one cache per query type.
 * Leverage existing cached selectors when possible. Intended for high-performance scenarios where cached `select`
 * operations reduce query computation cost.
 *
 * @param selector - the referentially stable selector function
 * @returns memoized selector function
 */
export const createWeakMapSelector = <S extends object, R>(selector: (state: S) => R) => {
    const weakMap = new WeakMap<S, R>();
    return (state: S): R => {
        if (!weakMap.has(state)) {
            weakMap.set(state, selector(state));
        }
        return weakMap.get(state) as R;
    };
};
